import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiClient } from '../../api/client';
import type {
  ContractGenerationSummary,
  ContractPlaceholder,
  ContractTemplateDetail,
  ContractTemplateSummary,
  GenerateContractResponse,
} from '../../types/contracts';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import './ContractGeneratorModal.css';

interface ContractGeneratorModalProps {
  open: boolean;
  onClose: () => void;
}

type StoredValuesMap = Record<string, Record<string, string>>;
type ContractOutputFormat = 'pdf' | 'docx';

const STORAGE_KEY = 'contract-generator-last-values-v1';

const loadStoredValues = (): StoredValuesMap => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

const saveStoredValues = (data: StoredValuesMap) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
};

const getTemplateDefaultName = (fileName: string) => fileName.replace(/\.(docx|pdf)$/i, '').trim();

const createEmptyValues = (placeholders: ContractPlaceholder[]) =>
  placeholders.reduce<Record<string, string>>((acc, item) => {
    acc[item.key] = '';
    return acc;
  }, {});

const getFieldElementId = (key: string) =>
  `contract-field-${key.replace(/[^a-zA-Z0-9_-]+/g, '-').replace(/-+/g, '-')}`;

const getFormatLabel = (format: 'docx' | 'pdf') => format.toUpperCase();

const getFileFormat = (file: File | null): 'docx' | 'pdf' | null => {
  if (!file) return null;
  return file.name.toLowerCase().endsWith('.pdf') ? 'pdf' : 'docx';
};

const toTemplateSummary = (template: ContractTemplateDetail): ContractTemplateSummary => ({
  id: template.id,
  name: template.name,
  original_filename: template.original_filename,
  placeholder_prefix: template.placeholder_prefix,
  placeholder_syntax: template.placeholder_syntax,
  source_format: template.source_format,
  placeholders_count: template.placeholders.length,
  next_number: template.next_number,
  number_placeholder_key: template.number_placeholder_key,
  client_slug_key: template.client_slug_key,
  created_count: template.created_count,
  last_number: template.last_number,
  updated_at: template.updated_at,
});

const downloadBlobResult = (result: GenerateContractResponse) => {
  const objectUrl = URL.createObjectURL(result.blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = result.filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objectUrl);
};

const getApiErrorMessage = (err: any, fallback: string) => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) {
    return detail;
  }
  if (detail && typeof detail?.message === 'string' && detail.message.trim()) {
    return detail.message;
  }
  if (typeof err?.message === 'string' && err.message.trim()) {
    return err.message;
  }
  return fallback;
};

const ContractGeneratorModal: React.FC<ContractGeneratorModalProps> = ({ open, onClose }) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const replaceFileInputRef = useRef<HTMLInputElement | null>(null);
  const storedValuesRef = useRef<StoredValuesMap>({});
  const selectedTemplateIdRef = useRef<number | null>(null);
  const [templates, setTemplates] = useState<ContractTemplateSummary[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<ContractTemplateDetail | null>(null);
  const [templateName, setTemplateName] = useState('');
  const [templatePrefix, setTemplatePrefix] = useState('##');
  const [templateNextNumber, setTemplateNextNumber] = useState('1');
  const [templateNumberPlaceholderKey, setTemplateNumberPlaceholderKey] = useState('contract_number');
  const [templateClientSlugKey, setTemplateClientSlugKey] = useState('customer_name');
  const [values, setValues] = useState<Record<string, string>>({});
  const [validationKeys, setValidationKeys] = useState<string[]>([]);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadName, setUploadName] = useState('');
  const [uploadNextNumber, setUploadNextNumber] = useState('1');
  const [uploadNumberPlaceholderKey, setUploadNumberPlaceholderKey] = useState('contract_number');
  const [uploadClientSlugKey, setUploadClientSlugKey] = useState('customer_name');
  const [replaceFile, setReplaceFile] = useState<File | null>(null);
  const [outputFormat, setOutputFormat] = useState<ContractOutputFormat>('pdf');
  const [storedValues, setStoredValues] = useState<StoredValuesMap>({});
  const [generations, setGenerations] = useState<ContractGenerationSummary[]>([]);
  const [listLoading, setListLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [generationsLoading, setGenerationsLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [replacingTemplate, setReplacingTemplate] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [deletingTemplateId, setDeletingTemplateId] = useState<number | null>(null);
  const [regeneratingGenerationId, setRegeneratingGenerationId] = useState<number | null>(null);
  const [loadingGenerationId, setLoadingGenerationId] = useState<number | null>(null);
  const [viewingGeneration, setViewingGeneration] = useState<ContractGenerationSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const uploadSourceFormat = useMemo(() => getFileFormat(uploadFile), [uploadFile]);
  const replaceSourceFormat = useMemo(() => getFileFormat(replaceFile), [replaceFile]);

  const lastValuesForSelectedTemplate = useMemo(() => {
    if (!selectedTemplateId) return null;
    return storedValues[String(selectedTemplateId)] || null;
  }, [selectedTemplateId, storedValues]);

  const selectedTemplateSummary = useMemo(
    () => templates.find((item) => item.id === selectedTemplateId) || null,
    [templates, selectedTemplateId],
  );

  const fillablePlaceholders = useMemo(() => {
    if (!selectedTemplate) return [];
    const numberKey = selectedTemplate.number_placeholder_key || 'contract_number';
    return selectedTemplate.placeholders.filter((item) => item.key !== numberKey);
  }, [selectedTemplate]);

  const numberPlaceholderMissing = useMemo(() => {
    if (!selectedTemplate?.number_placeholder_key) return false;
    return !selectedTemplate.placeholders.some((item) => item.key === selectedTemplate.number_placeholder_key);
  }, [selectedTemplate]);

  useEffect(() => {
    storedValuesRef.current = storedValues;
  }, [storedValues]);

  useEffect(() => {
    setViewingGeneration(null);
  }, [selectedTemplateId]);

  useEffect(() => {
    selectedTemplateIdRef.current = selectedTemplateId;
  }, [selectedTemplateId]);

  const loadGenerations = useCallback(async (templateId: number) => {
    setGenerationsLoading(true);
    try {
      const list = await apiClient.listContractGenerations(templateId);
      setGenerations(list);
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Не удалось загрузить историю договоров.'));
    } finally {
      setGenerationsLoading(false);
    }
  }, []);

  const loadTemplateDetail = useCallback(async (templateId: number, cacheMap?: StoredValuesMap) => {
    setDetailLoading(true);
    setError(null);
    try {
      const detail = await apiClient.getContractTemplate(templateId);
      setSelectedTemplate(detail);
      setTemplateName(detail.name);
      setTemplatePrefix(detail.placeholder_prefix || '');
      setTemplateNextNumber(String(detail.next_number || 1));
      setTemplateNumberPlaceholderKey(detail.number_placeholder_key || 'contract_number');
      setTemplateClientSlugKey(detail.client_slug_key || 'customer_name');
      setOutputFormat('pdf');
      setReplaceFile(null);
      if (replaceFileInputRef.current) {
        replaceFileInputRef.current.value = '';
      }
      const effectiveCache = cacheMap ?? storedValuesRef.current;
      const cachedValues = effectiveCache[String(templateId)] || {};
      setValues({
        ...createEmptyValues(detail.placeholders),
        ...cachedValues,
      });
      setValidationKeys([]);
      setViewingGeneration(null);
      await loadGenerations(templateId);
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Не удалось загрузить шаблон.'));
    } finally {
      setDetailLoading(false);
    }
  }, [loadGenerations]);

  const loadTemplates = useCallback(async (preferredTemplateId?: number, cacheMap?: StoredValuesMap) => {
    setListLoading(true);
    setError(null);
    try {
      const list = await apiClient.listContractTemplates();
      setTemplates(list);
      const currentSelectedTemplateId = selectedTemplateIdRef.current;
      const nextSelectedId =
        preferredTemplateId && list.some((item) => item.id === preferredTemplateId)
          ? preferredTemplateId
          : currentSelectedTemplateId && list.some((item) => item.id === currentSelectedTemplateId)
            ? currentSelectedTemplateId
            : list[0]?.id ?? null;
      setSelectedTemplateId(nextSelectedId);
      if (nextSelectedId) {
        await loadTemplateDetail(nextSelectedId, cacheMap);
      } else {
        setSelectedTemplate(null);
        setTemplateName('');
        setTemplatePrefix('');
        setTemplateNextNumber('1');
        setTemplateNumberPlaceholderKey('contract_number');
        setTemplateClientSlugKey('customer_name');
        setValues({});
        setGenerations([]);
      }
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Не удалось загрузить шаблоны.'));
    } finally {
      setListLoading(false);
    }
  }, [loadTemplateDetail]);

  useEffect(() => {
    if (!open) return;
    const initialStoredValues = loadStoredValues();
    setStoredValues(initialStoredValues);
    void loadTemplates(undefined, initialStoredValues);
  }, [loadTemplates, open]);

  useEffect(() => {
    if (!open || !selectedTemplateId) return;
    if (selectedTemplate?.id === selectedTemplateId) return;
    void loadTemplateDetail(selectedTemplateId);
  }, [loadTemplateDetail, open, selectedTemplate, selectedTemplateId]);

  const handleUploadFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    setUploadFile(file);
    setSuccessMessage(null);
    if (file) {
      setUploadName(getTemplateDefaultName(file.name));
    }
  };

  const handleUploadTemplate = async () => {
    if (!uploadFile) {
      setError('Выберите .docx или .pdf шаблон.');
      return;
    }
    if (!uploadName.trim()) {
      setError('Укажите имя шаблона.');
      return;
    }
    const parsedNextNumber = Number(uploadNextNumber);
    if (!Number.isInteger(parsedNextNumber) || parsedNextNumber < 1) {
      setError('Стартовый номер должен быть целым числом больше нуля.');
      return;
    }
    if (!uploadNumberPlaceholderKey.trim()) {
      setError('Укажите плейсхолдер номера договора.');
      return;
    }

    setUploading(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const created = await apiClient.uploadContractTemplate({
        file: uploadFile,
        name: uploadName.trim(),
        placeholderPrefix: '',
        placeholderSyntax: uploadSourceFormat === 'pdf' ? 'pdf_fields' : 'double_curly',
        nextNumber: parsedNextNumber,
        numberPlaceholderKey: uploadNumberPlaceholderKey.trim(),
        clientSlugKey: uploadClientSlugKey.trim(),
      });
      setUploadFile(null);
      setUploadName('');
      setUploadNextNumber('1');
      setUploadNumberPlaceholderKey('contract_number');
      setUploadClientSlugKey('customer_name');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      setSuccessMessage(`Шаблон «${created.name}» загружен.`);
      await loadTemplates(created.id);
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Не удалось загрузить шаблон.'));
    } finally {
      setUploading(false);
    }
  };

  const handleSaveTemplateSettings = async () => {
    if (!selectedTemplateId || !selectedTemplate) return;
    if (!templateName.trim()) {
      setError('Имя шаблона не может быть пустым.');
      return;
    }
    if (
      selectedTemplate.source_format === 'docx'
      && selectedTemplate.placeholder_syntax === 'prefix'
      && !templatePrefix.trim()
    ) {
      setError('Префикс плейсхолдеров не может быть пустым.');
      return;
    }
    const parsedNextNumber = Number(templateNextNumber);
    if (!Number.isInteger(parsedNextNumber) || parsedNextNumber < 1) {
      setError('Следующий номер должен быть целым числом больше нуля.');
      return;
    }
    if (!templateNumberPlaceholderKey.trim()) {
      setError('Плейсхолдер номера договора не может быть пустым.');
      return;
    }

    setSavingTemplate(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const updated = await apiClient.updateContractTemplate(selectedTemplateId, {
        name: templateName.trim(),
        placeholder_prefix:
          selectedTemplate.source_format === 'docx' && selectedTemplate.placeholder_syntax === 'prefix'
            ? templatePrefix.trim()
            : '',
        placeholder_syntax: selectedTemplate.placeholder_syntax,
        next_number: parsedNextNumber,
        number_placeholder_key: templateNumberPlaceholderKey.trim(),
        client_slug_key: templateClientSlugKey.trim(),
      });
      setSelectedTemplate(updated);
      setTemplates((prev) => prev.map((item) => (item.id === updated.id ? toTemplateSummary(updated) : item)));
      const cachedValues = storedValues[String(updated.id)] || {};
      setValues({
        ...createEmptyValues(updated.placeholders),
        ...cachedValues,
      });
      setSuccessMessage('Настройки шаблона сохранены.');
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Не удалось обновить шаблон.'));
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleDownloadTemplateFile = async () => {
    if (!selectedTemplateId) return;
    setDownloadingTemplate(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const result = await apiClient.downloadContractTemplateFile(selectedTemplateId);
      downloadBlobResult(result);
      setSuccessMessage(`Исходный шаблон «${result.filename}» скачан.`);
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Не удалось скачать шаблон.'));
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const handleReplaceTemplateFile = async () => {
    if (!selectedTemplateId || !selectedTemplate || !replaceFile) {
      setError('Выберите файл для замены текущего шаблона.');
      return;
    }
    if (replaceSourceFormat !== selectedTemplate.source_format) {
      setError(`Нужен файл ${getFormatLabel(selectedTemplate.source_format)}. Смену формата лучше делать новым шаблоном.`);
      return;
    }

    setReplacingTemplate(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const updated = await apiClient.replaceContractTemplateFile(selectedTemplateId, replaceFile);
      setReplaceFile(null);
      if (replaceFileInputRef.current) {
        replaceFileInputRef.current.value = '';
      }
      setSelectedTemplate(updated);
      setTemplates((prev) => prev.map((item) => (item.id === updated.id ? toTemplateSummary(updated) : item)));
      setValues({
        ...createEmptyValues(updated.placeholders),
        ...(storedValues[String(updated.id)] || {}),
      });
      setSuccessMessage('Файл шаблона заменён, поля обновлены.');
      await loadTemplates(updated.id);
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Не удалось заменить файл шаблона.'));
    } finally {
      setReplacingTemplate(false);
    }
  };

  const handleDeleteTemplate = async (templateId: number) => {
    const template = templates.find((item) => item.id === templateId);
    if (!template) return;
    if (!window.confirm(`Удалить шаблон «${template.name}»?`)) {
      return;
    }

    setDeletingTemplateId(templateId);
    setError(null);
    setSuccessMessage(null);
    try {
      await apiClient.deleteContractTemplate(templateId);
      const nextStoredValues = { ...storedValues };
      delete nextStoredValues[String(templateId)];
      saveStoredValues(nextStoredValues);
      setStoredValues(nextStoredValues);
      setSuccessMessage(`Шаблон «${template.name}» удалён.`);
      await loadTemplates(templateId === selectedTemplateId ? undefined : selectedTemplateId ?? undefined);
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Не удалось удалить шаблон.'));
    } finally {
      setDeletingTemplateId(null);
    }
  };

  const handleValueChange = (key: string, nextValue: string) => {
    setValues((prev) => ({ ...prev, [key]: nextValue }));
    setValidationKeys((prev) => prev.filter((item) => item !== key));
  };

  const handleResetValues = () => {
    if (!selectedTemplate) return;
    setValues(createEmptyValues(selectedTemplate.placeholders));
    setValidationKeys([]);
    setSuccessMessage(null);
    setViewingGeneration(null);
  };

  const handleCopyLastValues = () => {
    if (!selectedTemplate || !selectedTemplateId || !lastValuesForSelectedTemplate) return;
    setValues({
      ...createEmptyValues(selectedTemplate.placeholders),
      ...lastValuesForSelectedTemplate,
    });
    setValidationKeys([]);
    setViewingGeneration(null);
  };

  const handleGenerate = async () => {
    if (!selectedTemplate || !selectedTemplateId) {
      setError('Сначала выберите шаблон.');
      return;
    }

    const missingKeys = fillablePlaceholders
      .filter((item) => item.required && !String(values[item.key] || '').trim())
      .map((item) => item.key);

    if (missingKeys.length > 0) {
      setValidationKeys(missingKeys);
      setError('Заполните обязательные поля.');
      return;
    }

    setGenerating(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const result = await apiClient.generateContractTemplate(selectedTemplateId, {
        values,
        output_format: selectedTemplate.source_format === 'docx' ? outputFormat : 'pdf',
      });
      downloadBlobResult(result);

      const nextStoredValues = {
        ...storedValues,
        [String(selectedTemplateId)]: values,
      };
      setStoredValues(nextStoredValues);
      saveStoredValues(nextStoredValues);
      setSuccessMessage(`Файл «${result.filename}» готов.`);
      setViewingGeneration(null);
      await loadTemplates(selectedTemplateId, nextStoredValues);
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      const message = getApiErrorMessage(err, 'Не удалось сгенерировать договор.');
      const missing = detail?.missing_keys;
      if (Array.isArray(missing)) {
        setValidationKeys(missing);
      }
      setError(message);
    } finally {
      setGenerating(false);
    }
  };

  const handleRegenerate = async (generation: ContractGenerationSummary) => {
    setRegeneratingGenerationId(generation.id);
    setError(null);
    setSuccessMessage(null);
    try {
      const result = await apiClient.regenerateContractGeneration(generation.id, {
        output_format: generation.output_format,
      });
      downloadBlobResult(result);
      setSuccessMessage(`Файл «${result.filename}» пересобран из истории.`);
      if (selectedTemplateId) {
        await loadGenerations(selectedTemplateId);
      }
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Не удалось пересобрать договор из истории.'));
    } finally {
      setRegeneratingGenerationId(null);
    }
  };

  const handleViewGeneration = async (generation: ContractGenerationSummary) => {
    if (!selectedTemplate) return;
    setLoadingGenerationId(generation.id);
    setError(null);
    setSuccessMessage(null);
    try {
      const detail = await apiClient.getContractGeneration(generation.id);
      setValues({
        ...createEmptyValues(selectedTemplate.placeholders),
        ...detail.values,
      });
      setValidationKeys([]);
      setViewingGeneration(generation);
      setSuccessMessage(`Загружен договор № ${generation.contract_number}`);
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Не удалось загрузить детали договора.'));
    } finally {
      setLoadingGenerationId(null);
    }
  };

  const renderField = (placeholder: ContractPlaceholder) => {
    const value = values[placeholder.key] ?? '';
    const invalid = validationKeys.includes(placeholder.key);
    const fieldId = getFieldElementId(placeholder.key);

    if (placeholder.kind === 'textarea') {
      return (
        <textarea
          id={fieldId}
          className={`contract-generator__textarea ${invalid ? 'is-invalid' : ''}`}
          value={value}
          onChange={(event) => handleValueChange(placeholder.key, event.target.value)}
          rows={4}
          placeholder={placeholder.label}
        />
      );
    }

    return (
      <input
        id={fieldId}
        className={`contract-generator__input ${invalid ? 'is-invalid' : ''}`}
        value={value}
        onChange={(event) => handleValueChange(placeholder.key, event.target.value)}
        placeholder={placeholder.label}
      />
    );
  };

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => { if (!nextOpen) onClose(); }}>
      <DialogContent className="contract-generator__dialog">
        <DialogHeader className="contract-generator__header">
          <div className="contract-generator__header-copy">
            <div className="contract-generator__header-icon" aria-hidden="true">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7z" />
                <path d="M14 2v5h5" />
                <path d="M9 13h6" />
                <path d="M9 17h4" />
              </svg>
            </div>
            <div className="contract-generator__header-text">
              <DialogTitle>Договоры</DialogTitle>
              <span>{selectedTemplateSummary ? selectedTemplateSummary.name : 'Библиотека шаблонов и сборка договоров'}</span>
            </div>
          </div>
          <div className="contract-generator__header-stats">
            {selectedTemplate ? <span>Следующий № {selectedTemplate.next_number}</span> : null}
            {selectedTemplate ? <span>Создано {selectedTemplate.created_count}</span> : null}
          </div>
        </DialogHeader>

        <div className="contract-generator__layout">
          <aside className="contract-generator__sidebar">
            <section className="contract-generator__rail-section contract-generator__rail-section--templates">
              <div className="contract-generator__rail-header">
                <h3>Шаблоны</h3>
                {listLoading ? <span>Обновление</span> : null}
              </div>

              <div className="contract-generator__template-list">
                {templates.length === 0 ? (
                  <div className="contract-generator__empty-state contract-generator__empty-state--compact">
                    <strong>Шаблонов нет</strong>
                    <span>Добавьте DOCX или PDF в правой колонке.</span>
                  </div>
                ) : (
                  templates.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      className={`contract-generator__template-card ${selectedTemplateId === item.id ? 'is-active' : ''}`}
                      onClick={() => setSelectedTemplateId(item.id)}
                    >
                      <span className="contract-generator__template-name">{item.name}</span>
                      <span className="contract-generator__template-meta">
                        {getFormatLabel(item.source_format)} · {item.placeholders_count} полей · след. № {item.next_number}
                      </span>
                    </button>
                  ))
                )}
              </div>
            </section>

            <section className="contract-generator__rail-section contract-generator__rail-section--history">
              <div className="contract-generator__rail-header">
                <h3>История</h3>
                {generationsLoading ? <span>Загрузка</span> : null}
              </div>

              {selectedTemplate ? (
                generations.length > 0 ? (
                  <div className="contract-generator__generation-list">
                    {generations.map((generation) => (
                      <div key={generation.id} className={`contract-generator__generation-row ${viewingGeneration?.id === generation.id ? 'is-viewing' : ''}`}>
                        <button
                          type="button"
                          className="contract-generator__generation-main"
                          onClick={() => void handleViewGeneration(generation)}
                          disabled={loadingGenerationId === generation.id || regeneratingGenerationId === generation.id}
                        >
                          <strong>№ {generation.contract_number}</strong>
                          {generation.client_slug ? (
                            <span className="contract-generator__generation-slug">{generation.client_slug}</span>
                          ) : null}
                          <span>{new Date(generation.created_at).toLocaleDateString('ru-RU')}</span>
                        </button>
                        <button
                          type="button"
                          className="contract-generator__generation-download"
                          onClick={() => void handleRegenerate(generation)}
                          disabled={regeneratingGenerationId === generation.id || loadingGenerationId === generation.id}
                          title="Скачать"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                            <polyline points="7 10 12 15 17 10" />
                            <line x1="12" y1="15" x2="12" y2="3" />
                          </svg>
                        </button>
                        <span className="contract-generator__generation-format">{getFormatLabel(generation.output_format)}</span>
                        {generation.template_changed ? (
                          <span className="contract-generator__generation-warning">шаблон изменён</span>
                        ) : null}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="contract-generator__empty-state contract-generator__empty-state--compact">
                    <strong>Пока пусто</strong>
                    <span>Созданные договоры появятся здесь.</span>
                  </div>
                )
              ) : (
                <div className="contract-generator__empty-state contract-generator__empty-state--compact">
                  <strong>Выберите шаблон</strong>
                  <span>После выбора появится история.</span>
                </div>
              )}
            </section>
          </aside>

          <main className="contract-generator__workspace">
            <div className="contract-generator__workspace-top">
              <div>
                <h3>{selectedTemplate ? selectedTemplate.name : 'Заполнение договора'}</h3>
                {selectedTemplate ? (
                  <span>
                    Номер выдаст система: № {selectedTemplate.next_number}
                    {detailLoading ? ' · чтение шаблона' : ''}
                    {viewingGeneration ? (
                      <span className="contract-generator__viewing-badge">Просмотр № {viewingGeneration.contract_number}</span>
                    ) : null}
                  </span>
                ) : (
                  <span>Выберите шаблон слева, чтобы открыть поля.</span>
                )}
              </div>
              <div className="contract-generator__toolbar">
                <button
                  type="button"
                  className="contract-generator__ghost-button"
                  onClick={handleCopyLastValues}
                  disabled={!selectedTemplate || !lastValuesForSelectedTemplate}
                >
                  Прошлые значения
                </button>
                <button
                  type="button"
                  className="contract-generator__ghost-button"
                  onClick={handleResetValues}
                  disabled={!selectedTemplate}
                >
                  Сбросить
                </button>
              </div>
            </div>

            {selectedTemplate ? (
              <>
                {numberPlaceholderMissing ? (
                  <div className="contract-generator__message contract-generator__message--warning">
                    В шаблоне нет поля «{selectedTemplate.number_placeholder_key}»: номер сохранится в истории, но не попадёт в документ.
                  </div>
                ) : null}

                <div className="contract-generator__fields">
                  {fillablePlaceholders.length > 0 ? (
                    fillablePlaceholders.map((placeholder) => {
                      const invalid = validationKeys.includes(placeholder.key);
                      const fieldId = getFieldElementId(placeholder.key);
                      return (
                        <div
                          key={placeholder.key}
                          className={`contract-generator__field-row ${placeholder.kind === 'textarea' ? 'is-wide' : ''}`}
                        >
                          <label className="contract-generator__label" htmlFor={fieldId}>
                            {placeholder.label}
                            {placeholder.required ? <span className="contract-generator__required">*</span> : null}
                          </label>
                          {renderField(placeholder)}
                          {invalid ? <span className="contract-generator__field-error">Обязательное поле</span> : null}
                        </div>
                      );
                    })
                  ) : (
                    <div className="contract-generator__empty-state">
                      <strong>Нет полей для ручного ввода</strong>
                      <span>Можно сразу скачать договор с автоматическим номером.</span>
                    </div>
                  )}
                </div>

              </>
            ) : (
              <div className="contract-generator__empty-state contract-generator__empty-state--tall">
                <strong>Нет активного шаблона</strong>
                <span>Выберите шаблон слева или добавьте новый справа.</span>
              </div>
            )}
          </main>

          <aside className="contract-generator__inspector">
            <section className="contract-generator__inspector-section">
              <div className="contract-generator__inspector-header">
                <h3>Шаблон</h3>
                {selectedTemplate ? <span>{getFormatLabel(selectedTemplate.source_format)}</span> : null}
              </div>

              {selectedTemplate ? (
                <>
                  <label className="contract-generator__field">
                    <span className="contract-generator__label">Название</span>
                    <input
                      id="selected-template-name"
                      className="contract-generator__input"
                      value={templateName}
                      onChange={(event) => setTemplateName(event.target.value)}
                      placeholder="Имя шаблона"
                    />
                  </label>

                  <div className="contract-generator__settings-inline">
                    <label className="contract-generator__field">
                      <span className="contract-generator__label">След. номер</span>
                      <input
                        id="selected-template-next-number"
                        className="contract-generator__input"
                        type="number"
                        min={1}
                        step={1}
                        value={templateNextNumber}
                        onChange={(event) => setTemplateNextNumber(event.target.value)}
                      />
                    </label>
                    <label className="contract-generator__field">
                      <span className="contract-generator__label">Номер в шаблоне</span>
                      <input
                        id="selected-template-number-placeholder"
                        className="contract-generator__input contract-generator__input--mono"
                        value={templateNumberPlaceholderKey}
                        onChange={(event) => setTemplateNumberPlaceholderKey(event.target.value)}
                        placeholder="contract_number"
                      />
                    </label>
                    <label className="contract-generator__field">
                      <span className="contract-generator__label">Slug клиента</span>
                      <input
                        id="selected-template-client-slug-key"
                        className="contract-generator__input contract-generator__input--mono"
                        value={templateClientSlugKey}
                        onChange={(event) => setTemplateClientSlugKey(event.target.value)}
                        placeholder="customer_name"
                      />
                    </label>
                  </div>

                  {selectedTemplate.source_format === 'docx' && selectedTemplate.placeholder_syntax === 'prefix' ? (
                    <label className="contract-generator__field">
                      <span className="contract-generator__label">Префикс</span>
                      <input
                        id="selected-template-prefix"
                        className="contract-generator__input contract-generator__input--mono"
                        value={templatePrefix}
                        onChange={(event) => setTemplatePrefix(event.target.value)}
                        placeholder="##"
                      />
                    </label>
                  ) : null}

                  <div className="contract-generator__inspector-meta">
                    <span>Файл</span>
                    <strong>{selectedTemplate.original_filename}</strong>
                    <span>Поля</span>
                    <strong>{selectedTemplate.placeholders.length}</strong>
                    <span>Создано</span>
                    <strong>{selectedTemplate.created_count}</strong>
                  </div>

                  <div className="contract-generator__button-row">
                    <button
                      type="button"
                      className="contract-generator__secondary-button"
                      onClick={() => void handleSaveTemplateSettings()}
                      disabled={savingTemplate}
                    >
                      {savingTemplate ? 'Сохранение…' : 'Сохранить'}
                    </button>
                    <button
                      type="button"
                      className="contract-generator__secondary-button"
                      onClick={() => void handleDownloadTemplateFile()}
                      disabled={downloadingTemplate}
                    >
                      {downloadingTemplate ? 'Скачивание…' : 'Скачать шаблон'}
                    </button>
                  </div>

                  <div className="contract-generator__file-replace">
                    <label className="contract-generator__field">
                      <span className="contract-generator__label">Заменить файл</span>
                      <input
                        ref={replaceFileInputRef}
                        type="file"
                        accept={selectedTemplate.source_format === 'docx'
                          ? '.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                          : '.pdf,application/pdf'}
                        className="contract-generator__file-input"
                        onChange={(event) => setReplaceFile(event.target.files?.[0] ?? null)}
                      />
                    </label>
                    <button
                      type="button"
                      className="contract-generator__secondary-button"
                      onClick={() => void handleReplaceTemplateFile()}
                      disabled={!replaceFile || replacingTemplate}
                    >
                      {replacingTemplate ? 'Замена…' : 'Заменить'}
                    </button>
                  </div>

                  <button
                    type="button"
                    className="contract-generator__danger-button"
                    onClick={() => void handleDeleteTemplate(selectedTemplate.id)}
                    disabled={deletingTemplateId === selectedTemplate.id}
                  >
                    {deletingTemplateId === selectedTemplate.id ? 'Удаление…' : 'Удалить шаблон'}
                  </button>
                </>
              ) : (
                <div className="contract-generator__empty-state contract-generator__empty-state--compact">
                  <strong>Шаблон не выбран</strong>
                  <span>Настройки появятся после выбора.</span>
                </div>
              )}
            </section>

            <section className="contract-generator__inspector-section">
              <div className="contract-generator__inspector-header">
                <h3>Новый шаблон</h3>
                {uploadSourceFormat ? <span>{getFormatLabel(uploadSourceFormat)}</span> : null}
              </div>

              <label className="contract-generator__field">
                <span className="contract-generator__label">Файл</span>
                <input
                  ref={fileInputRef}
                  id="contract-template-file"
                  type="file"
                  accept=".docx,.pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/pdf"
                  className="contract-generator__file-input"
                  onChange={handleUploadFileChange}
                />
              </label>

              <label className="contract-generator__field">
                <span className="contract-generator__label">Название</span>
                <input
                  id="contract-template-name"
                  className="contract-generator__input"
                  value={uploadName}
                  onChange={(event) => setUploadName(event.target.value)}
                  placeholder="Договор монтажа"
                />
              </label>

              <div className="contract-generator__settings-inline">
                <label className="contract-generator__field">
                  <span className="contract-generator__label">Первый номер</span>
                  <input
                    id="contract-template-next-number"
                    className="contract-generator__input"
                    type="number"
                    min={1}
                    step={1}
                    value={uploadNextNumber}
                    onChange={(event) => setUploadNextNumber(event.target.value)}
                  />
                </label>
                <label className="contract-generator__field">
                  <span className="contract-generator__label">Плейсхолдер номера</span>
                  <input
                    id="contract-template-number-placeholder"
                    className="contract-generator__input contract-generator__input--mono"
                    value={uploadNumberPlaceholderKey}
                    onChange={(event) => setUploadNumberPlaceholderKey(event.target.value)}
                    placeholder="contract_number"
                  />
                </label>
                <label className="contract-generator__field">
                  <span className="contract-generator__label">Slug клиента</span>
                  <input
                    id="contract-template-client-slug-key"
                    className="contract-generator__input contract-generator__input--mono"
                    value={uploadClientSlugKey}
                    onChange={(event) => setUploadClientSlugKey(event.target.value)}
                    placeholder="customer_name"
                  />
                </label>
              </div>

              <button
                type="button"
                className="contract-generator__primary-button contract-generator__primary-button--full"
                onClick={() => void handleUploadTemplate()}
                disabled={uploading}
              >
                {uploading ? 'Загрузка…' : 'Добавить шаблон'}
              </button>
            </section>
          </aside>
        </div>

        <div className="contract-generator__footer">
          <div className="contract-generator__messages">
            {error ? (
              <div className="contract-generator__message contract-generator__message--error">{error}</div>
            ) : successMessage ? (
              <div className="contract-generator__message contract-generator__message--success">{successMessage}</div>
            ) : (
              <div className="contract-generator__ready-state">
                <span />
                <strong>{selectedTemplate ? 'Готово к генерации' : 'Выберите шаблон'}</strong>
                <em>{selectedTemplate ? 'Заполните обязательные поля и скачайте договор.' : 'Шаблоны доступны в левой колонке.'}</em>
              </div>
            )}
          </div>
          <div className="contract-generator__download-controls">
            {selectedTemplate?.source_format === 'docx' ? (
              <div className="contract-generator__output-toggle" role="group" aria-label="Формат результата">
                <button
                  type="button"
                  className={`contract-generator__toggle-button ${outputFormat === 'pdf' ? 'is-active' : ''}`}
                  onClick={() => setOutputFormat('pdf')}
                  disabled={generating}
                >
                  PDF
                </button>
                <button
                  type="button"
                  className={`contract-generator__toggle-button ${outputFormat === 'docx' ? 'is-active' : ''}`}
                  onClick={() => setOutputFormat('docx')}
                  disabled={generating}
                >
                  DOCX
                </button>
              </div>
            ) : null}
            <button
              type="button"
              className="contract-generator__primary-button"
              onClick={() => void handleGenerate()}
              disabled={!selectedTemplate || generating}
            >
              {generating
                ? 'Сборка…'
                : selectedTemplate
                  ? `Скачать ${selectedTemplate.source_format === 'docx' ? getFormatLabel(outputFormat) : getFormatLabel(selectedTemplate.source_format)}`
                  : 'Скачать'}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ContractGeneratorModal;
